/**
 * Hook that fetches a debounced preview of an indicator while the user
 * is editing it in the builder.
 *
 * The hook POSTs the in-memory `config` plus `chartType` to
 * `/reporting/indicators/{id}/preview/` after a 400 ms debounce. Calling
 * `refresh()` triggers an immediate re-fetch (e.g. from the Preview
 * button on the SaveBar).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  reportingAPI,
  type IndicatorBuilderConfig,
  type IndicatorPreviewResponse,
} from '../../../lib/api/reporting';

interface UseIndicatorPreviewResult {
  preview: IndicatorPreviewResponse | null;
  loading: boolean;
  /** Force an immediate fetch ignoring the debounce timer. */
  refresh: () => void;
}

const DEBOUNCE_MS = 400;

/**
 * Live preview for a builder draft.
 *
 * @param indicatorId - Existing DashboardIndicator id (the preview endpoint
 *   needs an id even though it does not persist anything).
 * @param config - The current builder config snapshot.
 * @param chartType - Chart type chosen in the picker.
 * @returns `{ preview, loading, refresh }`
 */
export function useIndicatorPreview(
  indicatorId: number | undefined,
  config: IndicatorBuilderConfig,
  chartType: string,
): UseIndicatorPreviewResult {
  const [preview, setPreview] = useState<IndicatorPreviewResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const timerRef = useRef<number | null>(null);
  const cancelledRef = useRef<boolean>(false);
  const tickRef = useRef<number>(0);

  const fetchNow = useCallback(() => {
    if (!indicatorId) return;
    const myTick = ++tickRef.current;
    setLoading(true);
    reportingAPI
      .previewIndicator(indicatorId, { config, chart_type: chartType })
      .then((res) => {
        if (cancelledRef.current || myTick !== tickRef.current) return;
        setPreview(res.data);
      })
      .catch(() => {
        if (cancelledRef.current || myTick !== tickRef.current) return;
        setPreview(null);
      })
      .finally(() => {
        if (cancelledRef.current || myTick !== tickRef.current) return;
        setLoading(false);
      });
  }, [indicatorId, config, chartType]);

  useEffect(() => {
    cancelledRef.current = false;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      fetchNow();
    }, DEBOUNCE_MS);
    return () => {
      cancelledRef.current = true;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [fetchNow]);

  const refresh = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    fetchNow();
  }, [fetchNow]);

  return { preview, loading, refresh };
}
